import React from "react";

import { readStrategyFields } from "./PaperStrategyCard";

/**
 * AdoptedAnalysisPanel — 채택(adopt)된 전략들을 모아 아이디어 도출을 돕는
 * 종합 분석 패널. (DAS-126 Phase A)
 *
 * 모든 계산은 클라이언트에서 수행한다 (백엔드 불필요):
 *   - 입력 adopted: mergeAdoptedWithMeta 결과 ({id, name, paradigm, summary, ...})
 *   - 입력 summary: paper-trader 라이브 summary (전략별 KPI 조인용)
 *
 * 보여주는 것:
 *   1) paradigm(전략 유형) 분포 — 어떤 알파 축이 반복 채택되는지
 *   2) 코인 분포 — 채택이 쏠린 종목
 *   3) 전략별 라이브 실적 표 (PnL/거래/WR/PF)
 *   4) 직교성·중복 분석 — 같은 (paradigm, 코인) 조합 중복 후보 + 주력 축
 *
 * 목적: 채택 전략의 공통 성공 요인을 드러내, 다음 가설(미탐색 조합)을 떠올리게 한다.
 */

const KNOWN_COINS = ["BTC", "ETH", "SOL", "XRP", "ADA", "DOGE", "AAVE"];

// 전략 id/이름/요약에서 종목을 추출한다. 없으면 paradigm 기반 기본값.
function extractCoin(strat) {
    const hay = `${strat.id} ${strat.name || ""} ${strat.summary || ""}`.toUpperCase();
    const found = KNOWN_COINS.filter((c) => hay.includes(c));
    if (found.length === 0) {
        return strat.paradigm === "Macro" || strat.paradigm === "Liq" ? "멀티" : "—";
    }
    return found.join("-");
}

// summary 에서 전략 id 의 라이브 KPI 를 찾는다. 없으면 null.
function liveKpi(summary, id) {
    const list = summary?.strategies ?? [];
    const hit = list.find((s) => readStrategyFields(s).id === id);
    return hit ? readStrategyFields(hit) : null;
}

function countBy(items, keyFn) {
    const out = {};
    for (const it of items) {
        const k = keyFn(it);
        out[k] = (out[k] || 0) + 1;
    }
    return Object.entries(out).sort((a, b) => b[1] - a[1]); // [key, count] desc
}

// 채택 전략을 BacktestResearch 가설 도출 스크립트 입력용 JSON 으로 내보낸다.
// (Phase B — derive_hypotheses_from_adopted.py 가 소비)
function downloadAdoptedExport(rows) {
    const payload = {
        kind: "bcr_adopted_strategies_export",
        version: 1,
        strategies: rows.map((r) => ({
            id: r.id,
            name: r.name || null,
            paradigm: r.paradigm || null,
            coin: r.coin,
            mechanism: r.mechanism || null,
            entry_exit: r.entryExit || null,
            adopted_at: r.adoptedAt || null,
            adopted_reason: r.adoptedReason || null,
            live: r.kpi
                ? {
                      pnl_krw: Math.round(r.kpi.pnl),
                      trades: r.kpi.trades,
                      win_rate: r.kpi.wr,
                      profit_factor: r.kpi.pf >= 0 ? r.kpi.pf : null,
                  }
                : null,
        })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "adopted_strategies.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function AdoptedAnalysisPanel({ adopted, summary }) {
    if (!adopted || adopted.length === 0) return null;

    // 코인 주입 + 라이브 KPI 조인
    const rows = adopted.map((s) => {
        const coin = extractCoin(s);
        const kpi = liveKpi(summary, s.id);
        return { ...s, coin, kpi };
    });

    const paradigmDist = countBy(rows, (r) => r.paradigm || "기타");
    const coinDist = countBy(rows, (r) => r.coin);
    const maxParadigm = paradigmDist.length ? paradigmDist[0][1] : 1;

    // 라이브 실적 집계 (거래가 있는 전략만 WR/PF 평균)
    const withTrades = rows.filter((r) => r.kpi && r.kpi.trades > 0);
    const totalPnl = rows.reduce((a, r) => a + (r.kpi?.pnl || 0), 0);
    const totalTrades = rows.reduce((a, r) => a + (r.kpi?.trades || 0), 0);
    const avgWR =
        withTrades.length > 0
            ? withTrades.reduce((a, r) => a + (r.kpi.wr || 0), 0) / withTrades.length
            : 0;
    const pfVals = withTrades.filter((r) => r.kpi.pf >= 0).map((r) => r.kpi.pf);
    const avgPF =
        pfVals.length > 0 ? pfVals.reduce((a, v) => a + v, 0) / pfVals.length : null;

    // 직교성·중복: 같은 (paradigm, 코인) 조합이 2개 이상이면 중복 후보
    const comboDist = countBy(rows, (r) => `${r.paradigm || "기타"} · ${r.coin}`).filter(
        ([, c]) => c > 1,
    );
    const topParadigm = paradigmDist.length ? paradigmDist[0][0] : "—";
    const topCoin = coinDist.length ? coinDist[0][0] : "—";

    const pnlClass = totalPnl > 0 ? "text-gain" : totalPnl < 0 ? "text-loss" : "text-content";

    // 커버리지 매트릭스: paradigm(행) × 코인(열). 빈칸 = 미탐색 조합 = 가설 아이디어 힌트.
    const paradigmKeys = paradigmDist.map(([p]) => p);
    const coinKeys = coinDist.map(([c]) => c);
    const cellCount = {};
    for (const r of rows) {
        const key = `${r.paradigm || "기타"}|${r.coin}`;
        cellCount[key] = (cellCount[key] || 0) + 1;
    }
    // 관측된 paradigm×coin 격자에서 빈칸 개수 (후보 조합 수)
    const gapCount =
        paradigmKeys.length * coinKeys.length -
        Object.keys(cellCount).filter((k) => cellCount[k] > 0).length;

    return (
        <section className="bg-surface-card border border-surface-border rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
                <span aria-hidden="true">💡</span>
                <h3 className="text-sm font-semibold text-content">채택 전략 종합 분석</h3>
                <span className="text-xs text-content-muted hidden sm:inline">
                    공통 성공 축을 찾아 다음 가설로 연결
                </span>
                <button
                    type="button"
                    onClick={() => downloadAdoptedExport(rows)}
                    aria-label="채택 전략을 가설 도출용 JSON 으로 내보내기"
                    className="ml-auto text-xs text-brand border border-brand/40 rounded px-2.5 py-1.5 min-h-9 hover:bg-brand-subtle transition-colors shrink-0"
                >
                    ↓ 가설 도출용 export
                </button>
            </div>

            {/* 집계 KPI */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Kpi label="채택 전략" value={`${rows.length} 종`} />
                <Kpi
                    label="라이브 합산 PnL"
                    value={`${Math.round(totalPnl).toLocaleString()} KRW`}
                    valueClass={pnlClass}
                />
                <Kpi label="평균 승률" value={`${(avgWR * 100).toFixed(1)}%`} />
                <Kpi
                    label="평균 PF"
                    value={avgPF != null ? avgPF.toFixed(2) : "—"}
                    sub={`거래 ${totalTrades}건`}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* paradigm 분포 */}
                <div>
                    <div className="text-xs text-content-muted mb-2 uppercase tracking-wide">
                        전략 유형(paradigm) 분포
                    </div>
                    <div className="space-y-1.5">
                        {paradigmDist.map(([p, c]) => (
                            <div key={p} className="flex items-center gap-2">
                                <span className="text-xs text-content-secondary w-24 shrink-0 truncate">
                                    {p}
                                </span>
                                <div className="flex-1 h-3 bg-surface-elevated rounded-sm overflow-hidden">
                                    <div
                                        className="h-full bg-brand rounded-sm"
                                        style={{ width: `${(c / maxParadigm) * 100}%` }}
                                    />
                                </div>
                                <span className="text-xs text-content tabular-nums w-6 text-right">
                                    {c}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 코인 분포 */}
                <div>
                    <div className="text-xs text-content-muted mb-2 uppercase tracking-wide">
                        종목 분포
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {coinDist.map(([coin, c]) => (
                            <span
                                key={coin}
                                className="text-xs bg-surface-elevated text-content-secondary px-2 py-1 rounded"
                            >
                                {coin}
                                <span className="ml-1 text-content tabular-nums">{c}</span>
                            </span>
                        ))}
                    </div>

                    {/* 직교성·중복 */}
                    <div className="text-xs text-content-muted mt-4 mb-1.5 uppercase tracking-wide">
                        직교성 · 중복
                    </div>
                    <ul className="text-xs text-content-secondary space-y-1">
                        <li>
                            주력 축:{" "}
                            <span className="text-content">
                                {topParadigm} / {topCoin}
                            </span>
                        </li>
                        {comboDist.length > 0 ? (
                            comboDist.map(([combo, c]) => (
                                <li key={combo} className="text-warning">
                                    중복 후보: {combo} — {c}종 (상관 위험, 자본 분산 점검)
                                </li>
                            ))
                        ) : (
                            <li className="text-gain">
                                조합 중복 없음 — 채택 전략이 직교적으로 분산됨
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            {/* 커버리지 매트릭스 (paradigm × 코인) — 빈칸 = 미탐색 조합 */}
            {paradigmKeys.length > 0 && coinKeys.length > 1 && (
                <div className="mt-4">
                    <div className="text-xs text-content-muted mb-2 uppercase tracking-wide">
                        커버리지 (유형 × 종목) —{" "}
                        <span className="text-warning">빈칸 {gapCount}개</span>는 미탐색 조합
                        (가설 후보)
                    </div>
                    <div className="overflow-x-auto">
                        <table className="text-xs border-collapse">
                            <thead>
                                <tr>
                                    <th className="p-1.5 text-left text-content-muted font-medium">
                                        유형 \ 종목
                                    </th>
                                    {coinKeys.map((c) => (
                                        <th
                                            key={c}
                                            className="p-1.5 text-center text-content-secondary font-medium"
                                        >
                                            {c}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paradigmKeys.map((p) => (
                                    <tr key={p}>
                                        <td className="p-1.5 text-content-secondary whitespace-nowrap">
                                            {p}
                                        </td>
                                        {coinKeys.map((c) => {
                                            const n = cellCount[`${p}|${c}`] || 0;
                                            return (
                                                <td
                                                    key={c}
                                                    className={`p-1.5 text-center tabular-nums border border-surface-border/40 ${
                                                        n > 0
                                                            ? "bg-brand-subtle text-brand font-semibold"
                                                            : "text-content-muted"
                                                    }`}
                                                    title={
                                                        n > 0
                                                            ? `${p} · ${c}: 채택 ${n}종`
                                                            : `${p} · ${c}: 미탐색 — 가설 후보`
                                                    }
                                                >
                                                    {n > 0 ? n : "·"}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* 전략별 라이브 실적 표 */}
            <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-content-muted text-left border-b border-surface-border">
                            <th className="py-1.5 pr-2 font-medium">전략</th>
                            <th className="py-1.5 px-2 font-medium">유형</th>
                            <th className="py-1.5 px-2 font-medium">종목</th>
                            <th className="py-1.5 px-2 font-medium text-right">PnL(KRW)</th>
                            <th className="py-1.5 px-2 font-medium text-right">거래</th>
                            <th className="py-1.5 px-2 font-medium text-right">WR</th>
                            <th className="py-1.5 pl-2 font-medium text-right">PF</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const k = r.kpi;
                            const pc =
                                (k?.pnl || 0) > 0
                                    ? "text-gain"
                                    : (k?.pnl || 0) < 0
                                      ? "text-loss"
                                      : "text-content-secondary";
                            return (
                                <tr key={r.id} className="border-b border-surface-border/50">
                                    <td className="py-1.5 pr-2 font-mono text-content">{r.id}</td>
                                    <td className="py-1.5 px-2 text-content-secondary">
                                        {r.paradigm || "—"}
                                    </td>
                                    <td className="py-1.5 px-2 text-content-secondary">{r.coin}</td>
                                    <td className={`py-1.5 px-2 text-right tabular-nums ${pc}`}>
                                        {k ? Math.round(k.pnl).toLocaleString() : "—"}
                                    </td>
                                    <td className="py-1.5 px-2 text-right tabular-nums text-content-secondary">
                                        {k ? k.trades : "—"}
                                    </td>
                                    <td className="py-1.5 px-2 text-right tabular-nums text-content-secondary">
                                        {k && k.trades > 0 ? `${(k.wr * 100).toFixed(0)}%` : "—"}
                                    </td>
                                    <td className="py-1.5 pl-2 text-right tabular-nums text-content-secondary">
                                        {k && k.pf >= 0 ? k.pf.toFixed(2) : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function Kpi({ label, value, sub, valueClass = "text-content" }) {
    return (
        <div className="bg-surface-elevated rounded p-2.5">
            <div className="text-xs text-content-muted">{label}</div>
            <div className={`text-sm font-semibold tabular-nums mt-0.5 ${valueClass}`}>
                {value}
            </div>
            {sub && <div className="text-xs text-content-muted mt-0.5">{sub}</div>}
        </div>
    );
}
